import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi } from '../../lib/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Sparkles, Loader2, Check, ArrowRight, Globe, Shield, FileText,
  Building2, ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';
import { formatDate } from '../../lib/utils';

export default function AIAnalysisPage() {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(null);
  const [expandedRec, setExpandedRec] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    industry: '',
    business_description: '',
    goals: [],
    annual_revenue: ''
  });

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    try {
      const res = await aiApi.getRecommendations();
      setRecommendations(res.data);
    } catch (err) {
      console.error('Failed to load recommendations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await aiApi.analyzeJurisdiction(formData);
      setRecommendations([res.data, ...recommendations]);
      setShowForm(false);
      setExpandedRec(res.data.id);
    } catch (err) {
      console.error('Analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApply = async (recId) => {
    setApplying(recId);
    try {
      await aiApi.applyRecommendation(recId);
      navigate('/entities');
    } catch (err) {
      console.error('Apply failed:', err);
    } finally {
      setApplying(null);
    }
  };

  const goals = [
    { id: 'tax_optimization', label: 'Tax Optimization' },
    { id: 'asset_protection', label: 'Asset Protection' },
    { id: 'international_expansion', label: 'International Expansion' },
    { id: 'privacy', label: 'Privacy' }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-analysis-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">AI Analysis</h1>
            <p className="text-slate-400 mt-1">Get intelligent jurisdiction and structure recommendations</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="gradient-accent hover:opacity-90"
            data-testid="new-analysis-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            New Analysis
          </Button>
        </div>

        {/* Analysis Form */}
        {showForm && (
          <div className="stat-card space-y-6" data-testid="analysis-form">
            <h2 className="text-xl font-semibold">Request AI Analysis</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-300">Industry</Label>
                <Input
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder="e.g., Technology, E-Commerce"
                  className="bg-slate-900 border-slate-800 h-11"
                  data-testid="analysis-industry-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Annual Revenue</Label>
                <Input
                  value={formData.annual_revenue}
                  onChange={(e) => setFormData({ ...formData, annual_revenue: e.target.value })}
                  placeholder="e.g., $1M - $5M"
                  className="bg-slate-900 border-slate-800 h-11"
                  data-testid="analysis-revenue-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Business Description</Label>
              <Textarea
                value={formData.business_description}
                onChange={(e) => setFormData({ ...formData, business_description: e.target.value })}
                placeholder="Describe your business operations, target markets, and key activities..."
                className="bg-slate-900 border-slate-800 min-h-[100px]"
                data-testid="analysis-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Goals</Label>
              <div className="flex flex-wrap gap-2">
                {goals.map(goal => (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      goals: prev.goals.includes(goal.id)
                        ? prev.goals.filter(g => g !== goal.id)
                        : [...prev.goals, goal.id]
                    }))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.goals.includes(goal.id)
                        ? 'bg-blue-500/20 border-blue-500 text-blue-400 border'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                    data-testid={`goal-${goal.id}`}
                  >
                    {goal.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                variant="ghost"
                onClick={() => setShowForm(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAnalyze}
                disabled={analyzing || !formData.industry || !formData.business_description}
                className="gradient-accent hover:opacity-90"
                data-testid="run-analysis-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Run Analysis
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Recommendations List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : recommendations.length === 0 ? (
          <div className="stat-card text-center py-12" data-testid="no-recommendations">
            <Sparkles className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No recommendations yet</h3>
            <p className="text-slate-400 mb-6">Run an AI analysis to get jurisdiction recommendations</p>
            <Button
              onClick={() => setShowForm(true)}
              className="gradient-accent hover:opacity-90"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Analysis
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {recommendations.map((rec) => (
              <div key={rec.id} className="recommendation-card" data-testid={`recommendation-${rec.id}`}>
                <div 
                  className="recommendation-header cursor-pointer flex items-center justify-between"
                  onClick={() => setExpandedRec(expandedRec === rec.id ? null : rec.id)}
                >
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="font-medium">
                        {rec.recommendations?.[0]?.jurisdiction || 'Analysis Result'}
                      </p>
                      <p className="text-sm text-slate-400">{formatDate(rec.created_at)}</p>
                    </div>
                  </div>
                  {expandedRec === rec.id ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>

                {expandedRec === rec.id && (
                  <div className="p-6 space-y-6">
                    {rec.recommendations?.map((r, idx) => (
                      <div key={idx} className="space-y-4">
                        {idx > 0 && <div className="border-t border-slate-800 pt-6" />}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Globe className="w-6 h-6 text-blue-400" />
                            <div>
                              <h3 className="text-lg font-semibold">{r.jurisdiction}</h3>
                              <p className="text-sm text-slate-400">
                                Confidence: {Math.round((r.confidence_score || 0.8) * 100)}%
                              </p>
                            </div>
                          </div>
                          {idx === 0 && (
                            <Button
                              onClick={() => handleApply(rec.id)}
                              disabled={applying === rec.id}
                              className="gradient-accent hover:opacity-90"
                              data-testid={`apply-recommendation-${rec.id}`}
                            >
                              {applying === rec.id ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <>
                                  Apply Structure
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </>
                              )}
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-300">
                              <Shield className="w-4 h-4 text-emerald-400" />
                              Key Benefits
                            </div>
                            <ul className="space-y-2">
                              {r.jurisdiction_benefits?.map((b, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                                  {b}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-300">
                              <FileText className="w-4 h-4 text-amber-400" />
                              Tax Advantages
                            </div>
                            <ul className="space-y-2">
                              {r.tax_advantages?.map((t, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                                  <Check className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                                  {t}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {r.recommended_structure && r.recommended_structure.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-300">
                              <Building2 className="w-4 h-4 text-blue-400" />
                              Recommended Structure
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {r.recommended_structure.map((entity, i) => (
                                <div key={i} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                  <p className="font-medium text-sm">{entity.name}</p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    {entity.type} • {entity.jurisdiction}
                                  </p>
                                  {entity.description && (
                                    <p className="text-xs text-slate-500 mt-2">{entity.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {r.compliance_notes && r.compliance_notes.length > 0 && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-sm font-medium text-amber-400 mb-2">
                              <AlertCircle className="w-4 h-4" />
                              Compliance Notes
                            </div>
                            <ul className="space-y-1">
                              {r.compliance_notes.map((note, i) => (
                                <li key={i} className="text-sm text-slate-400">• {note}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {r.reasoning && (
                          <div className="bg-slate-800/30 rounded-lg p-4">
                            <p className="text-sm text-slate-400">{r.reasoning}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
